"use client"

import { Activity, Clock, CheckCircle, XCircle, AlertCircle, MoreHorizontal } from "lucide-react"
import { useIntl } from "react-intl"
import useLastSubmission from "../hooks/useLastSubmission"

const getStatusIcon = (status) => {
  switch (status) {
    case true:
      return <CheckCircle className="h-4 w-4 text-emerald-600" />
    case false:
      return <XCircle className="h-4 w-4 text-red-600" />
    default:
      return <AlertCircle className="h-4 w-4 text-gray-500" />
  }
}

const getStatusColor = (status) => {
  switch (status) {
    case "active":
      return "bg-emerald-50 text-emerald-700 border-emerald-200"
    case "completed":
      return "bg-emerald-50 text-emerald-700 border-emerald-200"
    case "error":
      return "bg-red-50 text-red-700 border-red-200"
    default:
      return "bg-gray-50 text-gray-600 border-gray-200"
  }
}

function JobListItem({ job, selectedJob, onJobSelect, viewJob, intl, isRTL, refreshToken }) {
  const { data: lastSub, loading: lastLoading } = useLastSubmission({ jobId: job.id, includePayload: false, refreshToken })

  // NEW helper: abbreviate large numbers
  const abbreviate = (v) => {
    if (v === null || v === undefined) return undefined
    const num = Number(v)
    if (isNaN(num)) return v
    if (Math.abs(num) >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M"
    if (Math.abs(num) >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K"
    return num.toString()
  }

  const flow = lastSub?.caudalLps
  const totalizer = lastSub?.totalizadorM3
  const nivel = lastSub?.nivelFreaticoM

  const rawTs = lastSub?.frameTs ||
    (lastSub?.fechaMedicion
      ? `${lastSub.fechaMedicion}${lastSub?.horaMedicion ? "T" + lastSub.horaMedicion : ""}`
      : undefined)

  let tsDate
  if (rawTs) {
    // Normalize "YYYY-MM-DD HH:MM" -> "YYYY-MM-DDTHH:MM"
    const norm = rawTs.includes("T") ? rawTs : rawTs.replace(" ", "T")
    const d = new Date(norm)
    if (!isNaN(d.getTime())) tsDate = d
  }

  const tsFormatted = tsDate
    ? intl.formatDate(tsDate, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    })
    : undefined

  return (
    <div
      className={`cursor-pointer transition-all hover:bg-gray-100 rounded-lg border shadow-sm ${selectedJob.id === job.id ? "bg-white border-blue-200 shadow-sm" : "bg-gray-50 border-gray-200"}`}
      onClick={() => onJobSelect(job)}
    >
      <div className="p-4 relative text-start">
        <div
          className={`absolute top-2 ${isRTL ? "left-2 right-auto" : "right-2 left-auto"}`}
          onClick={(e) => {
            viewJob("edit", job.id)
            e.stopPropagation()
          }}
        >
          <MoreHorizontal className="h-4 w-4 text-gray-500 hover:text-gray-700 cursor-pointer" />
        </div>
        <div className={`flex flex-col gap-1 mb-1 ${isRTL ? "pl-6 pr-0" : "pr-6 pl-0"}`}>
          <div className="flex items-center gap-2">
            {getStatusIcon(job.enabled)}
            <h3 className="font-thin text-gray-900 text-sm">{job.region_code}</h3>
          </div>
          <h5 className="text-gray-700 text-xs font-thin">
            {job?.description?.length > 50
              ? `${job?.description?.substring(0, 50)}...`
              : job?.description}
          </h5>
        </div>
        <div className="mt-1 pt-1 border-t">
          {lastLoading && (
            <span className="text-[11px] text-gray-400 font-thin block text-start">
              {intl.formatMessage({ id: "dga.jobs.loading", defaultMessage: "Loading..." })}
            </span>
          )}
          {!lastLoading && !lastSub && (
            <span className="text-[11px] text-gray-400 font-thin block text-start">
              {intl.formatMessage({ id: "dga.jobs.noLast", defaultMessage: "No data" })}
            </span>
          )}
          {lastSub && (
            <div className="space-y-1 text-start">
              {tsFormatted && (
                <div
                  className="text-xs text-black font-medium leading-tight truncate"
                  dir={isRTL ? "rtl" : "ltr"}
                >
                  {tsFormatted}
                </div>
              )}
              <div
                dir="ltr"
                className={`text-[11px] leading-tight flex ${(
                  (totalizer !== undefined) +
                  (flow !== undefined) +
                  (nivel !== undefined)
                ) === 3 ? "justify-between" : "gap-3 flex-wrap"} text-gray-700`}
              >
                {totalizer !== undefined && (
                  <span className="whitespace-nowrap">
                    <span className="text-gray-600 font-thin">
                      {intl.formatMessage({ id: "dga.jobs.totalizer", defaultMessage: "Tot:" })}
                    </span>{" "}
                    <span className="font-thin text-gray-600">{abbreviate(totalizer)}</span>
                  </span>
                )}
                {flow !== undefined && (
                  <span className="whitespace-nowrap">
                    <span className="text-gray-600 font-thin">
                      {intl.formatMessage({ id: "dga.jobs.flow", defaultMessage: "Flow:" })}
                    </span>{" "}
                    <span className="font-thin text-gray-600">
                      {intl.formatNumber(flow, { useGrouping: true, maximumFractionDigits: 1 })}
                    </span>
                  </span>
                )}
                {nivel !== undefined && (
                  <span className="whitespace-nowrap">
                    <span className="text-gray-600 font-thin">
                      {intl.formatMessage({ id: "dga.jobs.waterLevel", defaultMessage: "WL:" })}
                    </span>{" "}
                    <span className="font-thin text-gray-600">
                      {intl.formatNumber(nivel, { useGrouping: true, maximumFractionDigits: 2 })}
                    </span>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default function JobsList({ jobs, selectedJob, onJobSelect, viewJob, refreshToken }) {
  const intl = useIntl()
  const jobsList = Array.isArray(jobs) ? jobs : []
  const isRTL = intl.locale?.startsWith("ar") ?? false

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className="w-80 h-full border-e border-gray-200 bg-gray-50 flex flex-col text-start"
    >
      <div className="p-4 h-[59px] border-b border-gray-200">
        <h1 className="text-xl font-semibold text-gray-900">
          {intl.formatMessage({ id: "dga.jobs.header", defaultMessage: "Job Monitoring" })}
        </h1>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-2">
          {jobsList.map((job) => (
            <JobListItem
              key={job.id}
              job={job}
              selectedJob={selectedJob}
              onJobSelect={onJobSelect}
              viewJob={viewJob}
              intl={intl}
              isRTL={isRTL}
              refreshToken={refreshToken}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
